import {Injectable, Injector} from '@angular/core';
import {AbstractModel, HttpService, JsonObject, JsonProperty} from 'projects/core/src/public-api';
import {Observable, Subject} from 'rxjs';
import {QBTransaction} from './qb-transaction'
import {CabinetsReportPage} from 'projects/app-misc/src/app/modules/reports/data/cabinets-report-page';
import {SearchBody} from 'projects/app-misc/src/app/modules/search/data/searchBody';
import {CabinetsComplexityPage} from 'projects/app-misc/src/app/modules/reports/data/cabinets-complexity-page';
import {
  CabinetComplexityVariation
} from 'projects/app-misc/src/app/modules/reports/data/cabinet-complexity-variation';
import {
  OverallReport,
  OverallReportPage
} from 'projects/app-misc/src/app/modules/reports/data/overall-report-page';
import {Any} from 'json2typescript';
import {SaleStat} from 'projects/app-misc/src/app/modules/reports/data/sale-stat';
import {ProjectReportStats} from './report-stats-page';
import {SalesGraph} from './sales-graph';
import moment from 'moment';
import {DatePeriod} from '../view/graphs-view/graphs-view.component';
import {QbEstimateDTO} from './qb-estimate-dto';
import {MissingEstimateProject} from './missing-estimate-project';

export class ReportsFilter {
  query?: string;
  start?: number;
  limit?: number;
  fromDate?: string;
  toDate?: string;
}

@JsonObject('ProjectCostRatioValue')
export class ProjectCostRatioValue extends AbstractModel {
  @JsonProperty(Number, 'directLaborCost')
  directLaborCost?: number

  @JsonProperty(Number, 'overheadLaborCost')
  overheadLaborCost?: number
}

@JsonObject('ProjectCostRatio')
export class ProjectCostRatio extends AbstractModel {
  @JsonProperty(ProjectCostRatioValue, 'value')
  value?: ProjectCostRatioValue;
}


@Injectable({
  providedIn: 'root',
})
export class ReportsService extends HttpService {
  public userFilter$ = new Subject<number>();


  public dates: DatePeriod[] = [{
    label: 'Custom',
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    finishDate: moment().endOf('month').format('YYYY-MM-DD'),
    defaultView: {
      label: 'By Day',
      name: 'by-day',
      dateFormat: 'D MMM',
      service: 'ByDay'
    }
  }, {
    label: 'This Month',
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    finishDate: moment().endOf('month').format('YYYY-MM-DD'),
    defaultView: {
      label: 'By Day',
      name: 'by-day',
      dateFormat: 'D MMM',
      service: 'ByDay'
    }
  }, {
    label: 'Last Month',
    startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
    finishDate: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
    defaultView: {
      label: 'By Day',
      name: 'by-day',
      dateFormat: 'D MMM',
      service: 'ByDay'
    }
  }, {
    label: 'Last Quarter',
    startDate: moment().subtract(3, 'month').startOf('quarter').format('YYYY-MM-DD'),
    finishDate: moment().subtract(3, 'month').endOf('quarter').format('YYYY-MM-DD'),
    defaultView: {
      label: 'by Week',
      name: 'by-week',
      dateFormat: 'D MMM',
      service: 'ByWeek'
    }
  }, {
    label: 'Last Year',
    startDate: moment().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
    finishDate: moment().subtract(1, 'year').endOf('year').format('YYYY-MM-DD'),
    defaultView: {
      label: 'by Month',
      name: 'by-month',
      dateFormat: 'MMMM YYYY',
      service: 'ByMonth'
    }
  }, {
    label: 'This Year',
    startDate: moment().startOf('year').format('YYYY-MM-DD'),
    finishDate: moment().endOf('year').format('YYYY-MM-DD'),
    defaultView: {
      label: 'by Month',
      name: 'by-month',
      dateFormat: 'MMMM YYYY',
      service: 'ByMonth'
    }
  }, {
    label: 'Last 3 Month',
    startDate: moment().subtract(3, 'month').startOf('month').format('YYYY-MM-DD'),
    finishDate: moment().startOf('month').format('YYYY-MM-DD'),
    defaultView: {
      label: 'by Week',
      name: 'by-week',
      dateFormat: 'D MMM',
      service: 'ByWeek'
    }
  }, {
    label: 'Last 6 Month',
    startDate: moment().subtract(6, 'month').startOf('month').format('YYYY-MM-DD'),
    finishDate: moment().startOf('month').format('YYYY-MM-DD'),
    defaultView: {
      label: 'by Month',
      name: 'by-month',
      dateFormat: 'MMM',
      service: 'ByMonth'
    }
  }, {
    label: 'Last 12 Month',
    startDate: moment().subtract(12, 'month').startOf('month').format('YYYY-MM-DD'),
    finishDate: moment().startOf('month').format('YYYY-MM-DD'),
    defaultView: {
      label: 'by Month',
      name: 'by-month',
      dateFormat: 'MMMM YYYY',
      service: 'ByMonth'
    }
  }];

  constructor(
    protected injector: Injector,
  ) {
    super(injector);
  }

  createProjectCostReport(projectId: number): Observable<OverallReport> {
    return this.postData(`/project-cost?projectId=${projectId}`, {}, OverallReport);
  }

  getProjectReports(startDate: string, endDate: string, start: number, limit: number, filter): Observable<ProjectReportStats> {
    const params = [];

    for (const prop in filter) {
      if (Object.prototype.hasOwnProperty.call(filter, prop)) {
        const value = filter[prop] as number[]
        if (typeof value !== 'undefined') {
          params.push(`${prop}=${value.join(',')}`)
        }
      }
    }
    return this.getData(`/qb/sales-stats/projects/filtered/${startDate}:${endDate}?start=${start}&limit=${limit}&${params.join('&')}`, ProjectReportStats);
  }

  getReportRates(): Observable<ProjectCostRatio> {
    return this.getData(`/parameter/by-clazz/PROJECT_COST_PARAMETER`, ProjectCostRatio);
  }

  getGraphByDate(startDate: string, endDate: string, period: string, users: number[]): Observable<SalesGraph[]> {
    return this.getData(`/qb/sales-stats/${period}/${startDate}:${endDate}?userIds=${users}`, SalesGraph);
  }

  getGraphByDate2(startDate: string, endDate: string, period: string, users: number[]): Observable<SaleStat[]> {
    return this.getData(`/qb/sales-stats/${period}/${startDate}:${endDate}?userIds=${users}`, SaleStat);
  }

  // getGraphByDate2(startDate: string, endDate: string, period: string, users: number[]): Observable<SaleStat[]> {
  //   return this.getData(`/qb/sales-stats/${period}2/${startDate}:${endDate}?userIds=${users}`, SaleStat);
  // }

  getTransactionsForUser(startDate: string, endDate: string, userId: number): Observable<QBTransaction[]> {
    return this.getData(`/qb/sales-stats/transactions/${startDate}:${endDate}?userId=${userId}`, QBTransaction);
  }

  getTransactionsForSchedule(startDate: string, endDate: string): Observable<QBTransaction[]> {
    return this.getData(`/qb/sales-stats/transactions/${startDate}:${endDate}/for-schedule`, QBTransaction);
  }

  getCabinetReports(filter: ReportsFilter): Observable<CabinetsReportPage> {
    return this.postData(`/cabinets-complexity/list/reports/search`, filter, CabinetsReportPage);
  }


  searchCabinetsComplexityTypes(start: number, limit: number, query?: string): Observable<CabinetsComplexityPage> {
    return this.postData(`/cabinets-complexity/list/search`, {
      start,
      limit,
      query
    } as SearchBody, CabinetsComplexityPage);
  }


  searchProjectReports(filter: ReportsFilter): Observable<OverallReportPage> {
    return this.postData(`/project-cost/list/search`, {
      start: filter.start,
      limit: filter.limit,
      query: filter.query
    } as SearchBody, OverallReportPage);
  }


  filterProjectReports(filter: ReportsFilter): Observable<OverallReportPage> {
    return this.postData(`/project-cost/list/filtered`, {
      start: filter.start,
      limit: filter.limit,
      fromDate: filter.fromDate,
      toDate: filter.toDate
    }, OverallReportPage);
  }

  updateProjectReport(report: OverallReport): Observable<OverallReport> {
    return this.putData(`/project-cost`, report, OverallReport);
  }

  updateProjectCostRatio(val: ProjectCostRatio): Observable<ProjectCostRatio> {
    return this.putData(`/parameter/by-clazz/PROJECT_COST_PARAMETER`, val, ProjectCostRatio);
  }

  updateComplexity(cabinetComplexityVariation: CabinetComplexityVariation): Observable<CabinetComplexityVariation> {
    return this.putData(`/cabinets-complexity/update`, cabinetComplexityVariation, CabinetComplexityVariation)
  }

  deleteReport(reportId: number): Observable<any> {
    return this.deleteData(`/project-cost/${reportId}`, Any)
  }

  getEstimates(dateFrom: string, dateTo: string): Observable<QbEstimateDTO[]> {
    return this.getData(`/qb/estimates/${dateFrom}:${dateTo}`, QbEstimateDTO);
  }

  getEstimatesByProjectId(projectId: number): Observable<QbEstimateDTO[]> {
    return this.getData(`/qb/estimates/project/${projectId}`, QbEstimateDTO);
  }

  splitEstimate(estimateId: number, percentageForNew: number, newEstimateTxnDate: string): Observable<QbEstimateDTO> {
    return this.postData(`/qb/estimates/${estimateId}/split?estimateId=${estimateId}&percentageForNew=${percentageForNew}&newEstimateTxnDate=${newEstimateTxnDate}`, {}, QbEstimateDTO);
  }

  deleteEstimate(estimateId: number): Observable<any> {
    return this.deleteData(`/qb/estimates/${estimateId}`, Any);
  }

  createEstimate(txnDate: string, projectId: number, amount: number): Observable<QbEstimateDTO> {
    return this.postData(`/qb/estimates/create?txnDate=${txnDate}&projectId=${projectId}&amount=${amount}`, {}, QbEstimateDTO);
  }

  updateEstimate(estimateId: number, updateData: Partial<QbEstimateDTO>): Observable<QbEstimateDTO> {
    return this.putData(`/qb/estimates/${estimateId}`, updateData, QbEstimateDTO);
  }

  getMissingEstimateProjects(dateFrom: string, dateTo: string): Observable<MissingEstimateProject[]> {
    return this.getData(`/project/list/missing-estimates?dateFrom=${dateFrom}&dateTo=${dateTo}`, MissingEstimateProject);
  }

}
